/**
*
*   Dilution Calculator
*       - The Dilution Calculator
*
**/
'use client';

import Config from '@/config';
import Button from '@/components/button';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useTranslations } from 'next-intl';


//Build the Element
export default function DilutionCalculator(){

	//The Current Step
	const [ step , setStep ] = useState(0);
	const [ state , setState ] = useState(0);

	//Translations
	const t = useTranslations();

	//Form Settings
	const { register, reset, setValue, getValues, setError, formState: { errors } } = useForm({
		defaultValues: {
			volume: 0
		}
	});

	const Form = {

		//Steps in the Form
		steps: [
			{
				name: 'formula',
				text: t('Components.Calculator.steps.formula')
			},{
				name: 'sensitivity',
				text: t('Components.Calculator.steps.sensitivity')
			},{
				name: 'strength',
				text: t('Components.Calculator.steps.strength')
			},{
				name: 'volume',
				text: t('Components.Calculator.steps.volume')
			}
		],

		//The Ratios
		ratios: {
			'2-6':      {
				'gentle':       0.5,
				'casual':       null,
				'moderate':     null,
				'targeted':     null
			},
			'7-12':     {
				'gentle':       1,
				'casual':       null,
				'moderate':     null,
				'targeted':     null
			},
			'13-18':    {
				'gentle':       1,
				'casual':       2,
				'moderate':     null,
				'targeted':     null
			},
			'19+':       {
				'gentle':       1,
				'casual':       2,
				'moderate':     3,
				'targeted':     5
			}

		},

		//Ages
		formulas: {
			'2-6':      t('Components.Calculator.formulas.2-6'),
			'7-12':     t('Components.Calculator.formulas.7-12'),
			'13-18':    t('Components.Calculator.formulas.13-18'),
			'19+':      t('Components.Calculator.formulas.19+')
		},

		//Strengths
		strengths: {
			'gentle':   t('Components.Calculator.strengths.gentle'),
			'casual':   t('Components.Calculator.strengths.casual'),
			'moderate': t('Components.Calculator.strengths.moderate'),
			'targeted': t('Components.Calculator.strengths.targeted')
		},

		//Error
		error: ({ message }) => <p className="error">{ message }</p> ,

		//A Thrown Error Message
		thrown: ( message ) => setError('root.serverError',{
			type: 500,
			message: `${ t('Errors.unknown')  } ${  message }`
		}),

		//Go to next step
		next: () => {
			const value = getValues( Form.steps[ step ].name );
			const number  = (typeof value === 'number');
			if( ( number && value > 0 ) || ( !number && value != '' ) ){

				//Go to next step
				setStep( step + 1 );

			}else{

				//Set an Error
				setError( Form.steps[ step ].name , {
					type: 'required',
					message:  t('Forms.Errors.required',{
						type: Form.steps[ step ].text
					})
				});

			}
		},

		//Go to prev step
		prev: () => setStep( step - 1 ),

		reset: () => {
			reset();
			setStep(0);
		}
	};

	//Return
	return (
		<>
			<div className="calculator">

				{/* Type of Usage */}
				{ step == 0 && (
					<div className="step">

						<h2>
							{ t('Components.Calculator.title') }
							<small>{ t('Components.Calculator.content.formula.instructions') }</small>
						</h2>

						<p>{ t('Components.Calculator.content.formula.content') }</p>

						<div className="fields">
							<div className="field select">
								<select {...register('formula')}>
									<option value="">{ t('Components.Calculator.content.formula.select') }</option>
									{ Object.keys( Form.formulas ).map( ( key , i ) => {
										return (<option key={i} value={ key }>{ Form.formulas[ key ] }</option>);
									}) }
								</select>
								<ErrorMessage errors={errors} name="formula" render={ Form.error } />
							</div>
						</div>

					</div>
				)}

				{/* Photosensitivity */}
				{ step == 1 && (
					<div className="step">

						<h2>
							{ t('Components.Calculator.title') }
							<small>{ t('Components.Calculator.content.sensitivity.instructions') }</small>
						</h2>

						<p>{ t('Components.Calculator.content.sensitivity.content') }</p>

						<ul>
							<li>
								<strong>Angelica:</strong> 0.8%
							</li>
							<li>
								<strong>Bergamot (Not FCF):</strong> 0.4%
							</li>
							<li>
								<strong>Orange:</strong> 1.25%
							</li>
							<li>
								<strong>Cumin:</strong> 0.4%
							</li>
							<li>
								<strong>Grapefruit:</strong> 4%
							</li>
							<li>
								<strong>Lemon:</strong> 2%
							</li>
							<li>
								<strong>Lime:</strong> 2%
							</li>
							<li>
								<strong>Mandarin:</strong> 0% (any)
							</li>
							<li>
								<strong>Rue:</strong> 15%
							</li>
						</ul>

						<div className="fields">
							<div className="field select">
								<select {...register('sensitivity')}>
									<option value="">{ t('Components.Calculator.content.sensitivity.select') }</option>
									<option value="yes">
										{ t('Components.Calculator.content.sensitivity.yes') }
									</option>
									<option value="no">
										{ t('Components.Calculator.content.sensitivity.no') }
									</option>
								</select>
								<ErrorMessage errors={errors} name="sensitivity" render={ Form.error } />
							</div>
						</div>

					</div>
				)}

				{/* Strength */}
				{ step == 2 && (
					<div className="step">

						<h2>
							{ t('Components.Calculator.title') }
							<small>{ t('Components.Calculator.content.strength.instructions') }</small>
						</h2>

						<p>{ t('Components.Calculator.content.strength.content') }</p>

						<div className="fields">
							<div className="field select">
								<select {...register('strength',{
									onChange: (value) => setValue('ratio' , Form.ratios[ getValues('formula') ][ getValues('strength') ] )
								})}>
									<option value="">{ t('Components.Calculator.content.strength.select') }</option>
									{ Object.keys( Form.strengths ).map( ( key , i ) => {
										const ratio =  Form.ratios[ getValues('formula') ];
										if( ratio[ key ] ) return (<option key={i} value={ key }>{ Form.strengths[ key ] }</option>);
										return null;
									}) }
								</select>
								<ErrorMessage errors={errors} name="strength" render={ Form.error } />
							</div>
						</div>

					</div>
				)}


				{/* Volume */}
				{ step == 3 && (
					<div className="step">

						<h2>
							{ t('Components.Calculator.title') }
							<small>{ t('Components.Calculator.content.volume.instructions') }</small>
						</h2>

						<p>{ t.rich('Components.Calculator.content.volume.content', Config.i18n.html) }</p>

						<div className="fields">
							<div className="field">
								<input type="number" {...register('volume')} />
							</div>
							<div className="field" style={{ maxWidth: '100px' }}>
								<select name="type" {...register('type')}>
									<option value="ml">ml</option>
									<option value="fl oz">fl oz</option>
									<option value="l">litre / kg</option>
								</select>
							</div>
						</div>
						<ErrorMessage errors={errors} name="volume" render={ Form.error } />

					</div>
				)}


				{/* Results */}
				{ step == 4 && (
					<div className="step">

						<h2>
							{ t('Components.Calculator.title') }
							<small>{ t.rich('Components.Calculator.content.results.instructions', Config.i18n.html) }</small>
						</h2>

						<p>{ t.rich('Components.Calculator.content.results.content', Config.i18n.html) }</p>

						{(() => {

							//Get all Values
							const values = getValues();
							let ml = 0;

							//Get the volume calculation
							switch( values.type ){
								case 'fl oz':
									ml  = ( values.volume * 29.574 );
									break;

								case 'l':
									ml = ( values.volume * 1000 );
									break;

								default:
									ml = values.volume;
									break;
							}

							//Get the Strength
							const ratio = Form.ratios[ values.formula ];

							//Get the Dilution
							const dilution = ( values.ratio || Form.ratios[ values.formula ][ values.strength ] );

							//Get the required ratios
							const required = {
								ml:     ml * ( dilution / 100 ),
								drops:  ( ml * ( dilution / 100 ) ) * 20
							};

							//The Ratios options
							const options     = [];
							let starting    = Form.ratios[ values.formula ][ values.strength ];

							//Get the Dilution
							while( starting >= 0.25 ){

								options.push(<option value={ starting }>{ starting }%</option>);

								starting = starting - 0.25;

							}

							return (
								<table cellPadding="0" cellSpacing="0">
									<thead>
										<tr>
											<th>{ t('Components.Calculator.content.results.question') }</th>
											<th>{ t('Components.Calculator.content.results.entry') }</th>
										</tr>
									</thead>
									<tfoot>
										<tr className="head">
											<td>
												{ t('Components.Calculator.content.results.recommendation') }
											</td>
											<td>
												{ t('Components.Calculator.content.results.value') }
											</td>
										</tr>
										<tr>
											<td>
												{ t('Components.Calculator.content.results.ratio') }
											</td>
											<td>
												<div className="fields">
													<div className="field">
														<select name="ratio" {...register('ratio',{
															onChange: () => setState( state + 1 )
														})}>{
															options
														}</select>
													</div>
												</div>
											</td>
										</tr>
										<tr>
											<td>
												{ t('Components.Calculator.content.results.total-ml') }
											</td>
											<td>
												{ ( Math.floor( required.ml * 100 ) / 100 ) } ml
											</td>
										</tr>
										<tr>
											<td>
												{ t('Components.Calculator.content.results.total-drops') }
											</td>
											<td>
												{ ( Math.floor( required.drops * 100 ) / 100 ) }
											</td>
										</tr>
										{ values.sensitivity == 'yes' && (
										<tr>
											<td>
												{ t('Components.Calculator.content.results.other-notes') }
											</td>
											<td>
												<small>{ t('Components.Calculator.content.results.phototoxic') }</small>
											</td>
										</tr>
										)}
									</tfoot>
									<tbody>
										<tr>
											<td>{ t('Components.Calculator.content.results.created-for') }</td>
											<td>{ Form.formulas[ values.formula ] }</td>
										</tr>
										<tr>
											<td>{ t('Components.Calculator.content.results.purpose') }</td>
											<td>{ Form.strengths[ values.strength ] }</td>
										</tr>
										<tr>
											<td>{ t('Components.Calculator.content.results.total-volume') }</td>
											<td>{
												values.type == 'ml' ?
													`${ values.volume  } ml`
												:
													`${ values.volume  } ${  values.type  } ( ${  ml  }ml)`
											}</td>
										</tr>
									</tbody>
								</table>
							);

						})()}

						<p className="footer">
							<small>{ t.rich('Components.Calculator.content.results.footer', Config.i18n.html) }</small>
						</p>

					</div>
				)}


				<div className="buttons">

					<div>
						<Button onClick={ () => Form.prev() } disabled={ step == 0 } arrow="reverse">{ t('Navigation.back') }</Button>
						|
						<Button onClick={ () => Form.reset() } disabled={ step == 0 } inverted>{ t('Components.Calculator.start-over') }</Button>
					</div>

					<Button onClick={ () => Form.next() } disabled={ step >= 4 } arrow>{ t('Navigation.next') }</Button>

				</div>

			</div>
		</>
	);

}